import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightIcon, FileText } from 'lucide-react';
import { api } from '../services/api';

const PAGE_SIZE = 100;

const ACTION_NAMES: Record<number, string> = {
  1:  'CREATE',
  2:  'UPDATE',
  3:  'DEACTIVATE',
  4:  'ARCHIVE',
  5:  'VIEW_RESTRICTED',
  6:  'EXPORT',
  7:  'EMAIL_SENT',
  8:  'FLAG',
  9:  'FLAG_RESOLVED',
  10: 'BULK_IMPORT',
  11: 'HR_SYNC',
  12: 'LOGIN',
  13: 'REASSIGN_AGENCY',
  14: 'VERIFY',
  15: 'PASSWORD_RESET',
  16: 'CONTACT_SENT',
};

const ACTION_DISPLAY: Record<string, string> = {
  VERIFY:         '✓ VERIFY',
  PASSWORD_RESET: '🔑 Password Reset',
  CONTACT_SENT:   '📤 Contact Sent',
  LOGIN:          '🔐 Login',
};

const ACTION_COLORS: Record<string, { bg: string; color: string }> = {
  CREATE:          { bg: 'var(--green-50)', color: 'var(--green)' },
  UPDATE:          { bg: 'var(--sky-50)', color: 'var(--electric)' },
  VERIFY:          { bg: 'var(--green-50)', color: 'var(--green)' },
  DEACTIVATE:      { bg: 'var(--hazard-50)', color: 'var(--hazard-deep)' },
  ARCHIVE:         { bg: '#fce4ec', color: '#880E4F' },
  FLAG:            { bg: 'var(--amber-50)', color: '#F57F17' },
  FLAG_RESOLVED:   { bg: 'var(--green-50)', color: 'var(--green)' },
  VIEW_RESTRICTED: { bg: '#f3e5f5', color: '#6A1B9A' },
  EXPORT:          { bg: 'var(--sky-50)', color: 'var(--electric)' },
  EMAIL_SENT:      { bg: '#e0f7fa', color: '#006064' },
  BULK_IMPORT:     { bg: 'var(--sky-50)', color: 'var(--electric)' },
  HR_SYNC:         { bg: 'var(--sky-50)', color: 'var(--electric)' },
  LOGIN:           { bg: 'var(--gray-100)', color: 'var(--gray-700)' },
  REASSIGN_AGENCY: { bg: 'var(--hazard-50)', color: 'var(--hazard-deep)' },
  PASSWORD_RESET:  { bg: '#fdf4ff', color: '#7e22ce' },
  CONTACT_SENT:    { bg: '#e0f7fa', color: '#006064' },
};

interface AuditEntry {
  id: string;
  entity_type: string;
  entity_id: string | null;
  action_id: number;
  action_name: string;
  changed_by: string;
  changed_by_name: string;
  changed_by_email: string;
  changed_at: string;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  ip_address: string | null;
  notes: string | null;
}

function toLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function renderDiff(
  old_value: Record<string, unknown> | null,
  new_value: Record<string, unknown> | null,
  notes: string | null
): React.ReactNode {
  const hasOld = old_value && Object.keys(old_value).length > 0;
  const hasNew = new_value && Object.keys(new_value).length > 0;

  // Determine if this is a field-diff format: { field: old, field: new } (flat old/new objects)
  // vs a single new_value blob
  if (hasOld && hasNew) {
    const oldKeys = Object.keys(old_value!);
    const newKeys = Object.keys(new_value!);
    const allKeys = Array.from(new Set([...oldKeys, ...newKeys]));

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {notes && (
          <div style={{ fontSize: 12, marginBottom: 4 }}>
            <strong>Notes:</strong> {notes}
          </div>
        )}
        {allKeys.map(field => {
          const oldVal = old_value![field];
          const newVal = new_value![field];
          if (oldVal === newVal) return null;
          return (
            <div key={field} style={{ fontSize: 12, display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, color: 'var(--gray-700)', minWidth: 140 }}>{toLabel(field)}:</span>
              <span style={{ color: 'var(--red)', textDecoration: 'line-through' }}>
                {oldVal == null ? '(empty)' : String(oldVal)}
              </span>
              <span style={{ color: 'var(--gray-500)' }}>→</span>
              <span style={{ color: 'var(--green)', fontWeight: 500 }}>
                {newVal == null ? '(empty)' : String(newVal)}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  // Single new_value blob (create / verify / password reset / etc.)
  if (hasNew) {
    const nv = new_value!;

    // Contact sent format
    if ('sent_to_email' in nv) {
      return (
        <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 5, color: 'var(--gray-700)' }}>
          {notes && <div><strong>Notes:</strong> {notes}</div>}
          <div>
            <span style={{ fontWeight: 600 }}>Sent to:</span>{' '}
            {nv.sent_to_name ? `${String(nv.sent_to_name)} (${String(nv.sent_to_email)})` : String(nv.sent_to_email)}
          </div>
          <div>
            <span style={{ fontWeight: 600 }}>{(nv.contact_count as number) === 1 ? 'Contact:' : 'Contacts:'}</span>{' '}
            {String(nv.contacts_sent)}
          </div>
          {(nv.contact_count as number) > 1 && (
            <div style={{ color: 'var(--gray-500)', fontSize: 12 }}>
              {String(nv.contact_count)} contacts sent in one email with .vcf attachments
            </div>
          )}
        </div>
      );
    }

    // Verification format
    if ('last_verified_at' in nv) {
      return (
        <div style={{ fontSize: 12, color: 'var(--gray-700)', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {notes && <div><strong>Notes:</strong> {notes}</div>}
          <div>Verified at: {new Date(String(nv.last_verified_at)).toLocaleString()}</div>
        </div>
      );
    }

    // Password reset format
    if ('email' in nv && ('reset_by' in nv || 'action' in nv)) {
      return (
        <div style={{ fontSize: 12, color: 'var(--gray-700)', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {notes && <div><strong>Notes:</strong> {notes}</div>}
          <div>Reset for: {String(nv.email)}</div>
          {nv.reset_by && <div>Reset by: {String(nv.reset_by)}</div>}
        </div>
      );
    }

    // Generic new-value display — key: value pairs, plain language
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {notes && (
          <div style={{ fontSize: 12, marginBottom: 4 }}>
            <strong>Notes:</strong> {notes}
          </div>
        )}
        {Object.entries(nv)
          .filter(([, v]) => v !== null && v !== undefined)
          .map(([k, v]) => (
            <div key={k} style={{ fontSize: 12, color: 'var(--gray-700)' }}>
              <span style={{ fontWeight: 600 }}>{toLabel(k)}:</span>{' '}
              {typeof v === 'object' ? JSON.stringify(v) : String(v)}
            </div>
          ))}
      </div>
    );
  }

  // Notes only
  if (notes) {
    return <div style={{ fontSize: 12, color: 'var(--gray-700)' }}>{notes}</div>;
  }

  return null;
}

const AuditLogPage: React.FC = () => {
  const [entityType, setEntityType] = useState('');
  const [actionId, setActionId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({ entityType: '', actionId: '', dateFrom: '', dateTo: '' });
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Fix 1: explicit enabled:true + staleTime to ensure the query fires immediately on
  // mount and doesn't show an empty state due to stale-while-revalidate transitions.
  const { data, isLoading, error } = useQuery({
    queryKey: ['audit-log', filters, offset],
    queryFn: () => api.getAuditLog({
      entity_type: filters.entityType || undefined,
      action_id:   filters.actionId   ? Number(filters.actionId) : undefined,
      date_from:   filters.dateFrom   || undefined,
      date_to:     filters.dateTo     || undefined,
      limit:       PAGE_SIZE,
      offset,
    }),
    enabled:   true,
    staleTime: 30_000,
  });

  const entries: AuditEntry[] = data?.data ?? [];
  const total: number = data?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const applyFilters = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setOffset(0);
    setFilters({ entityType, actionId, dateFrom, dateTo });
  }, [entityType, actionId, dateFrom, dateTo]);

  const exportCSV = () => {
    const headers = ['Date', 'Action', 'Entity Type', 'Entity ID', 'Changed By', 'IP Address'];
    const rows = entries.map(e => [
      new Date(e.changed_at).toLocaleString(),
      e.action_name ?? ACTION_NAMES[e.action_id] ?? String(e.action_id),
      e.entity_type,
      e.entity_id,
      e.changed_by_name || e.changed_by_email?.split('@')[0] || '',
      e.ip_address ?? '',
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ paddingBottom: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900 }}>Audit Log</h2>
        <button className="btn btn-secondary" onClick={exportCSV} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FileText size={14} />
          Export CSV
        </button>
      </div>

      {/* Filter bar */}
      <form onSubmit={applyFilters}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase' }}>Entity Type</label>
            <select
              value={entityType}
              onChange={e => setEntityType(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--gray-300)', fontSize: 14 }}
            >
              <option value="">All</option>
              <option value="contacts">Contacts</option>
              <option value="agencies">Agencies</option>
              <option value="users">Users</option>
              <option value="roles">Roles</option>
              <option value="lists">Lists</option>
              <option value="flags">Flags</option>
              <option value="pending_changes">Pending Changes</option>
              <option value="update_requests">Update Requests</option>
              <option value="settings">Settings</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase' }}>Action</label>
            <select
              value={actionId}
              onChange={e => setActionId(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--gray-300)', fontSize: 14 }}
            >
              <option value="">All Actions</option>
              <option value="1">CREATE</option>
              <option value="2">UPDATE</option>
              <option value="3">DEACTIVATE</option>
              <option value="4">ARCHIVE</option>
              <option value="5">VIEW_RESTRICTED</option>
              <option value="6">EXPORT</option>
              <option value="7">EMAIL_SENT</option>
              <option value="8">FLAG</option>
              <option value="9">FLAG_RESOLVED</option>
              <option value="10">BULK_IMPORT</option>
              <option value="11">HR_SYNC</option>
              <option value="12">LOGIN</option>
              <option value="13">REASSIGN_AGENCY</option>
              <option value="14">VERIFY</option>
              <option value="15">Password Reset</option>
              <option value="16">Contact Sent</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase' }}>From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--gray-300)', fontSize: 14 }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase' }}>To</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--gray-300)', fontSize: 14 }}
            />
          </div>

          <button type="submit" className="btn btn-primary">Apply Filters</button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setEntityType(''); setActionId(''); setDateFrom(''); setDateTo('');
              setOffset(0);
              setFilters({ entityType: '', actionId: '', dateFrom: '', dateTo: '' });
            }}
          >
            Clear
          </button>
        </div>
      </form>

      {/* Results count */}
      <div style={{ fontSize: 14, color: 'var(--gray-500)', marginBottom: 8 }}>
        {isLoading ? 'Loading…' : `${total.toLocaleString()} entries`}
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 24 }}></th>
              <th>Date / Time</th>
              <th>Action</th>
              <th>Entity Type</th>
              <th>Entity ID</th>
              <th>Changed By</th>
              <th>IP Address</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--gray-500)' }}>Loading…</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--red)' }}>Failed to load audit log.</td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--gray-500)' }}>No entries found.</td>
              </tr>
            ) : entries.map(entry => {
              const actionName = entry.action_name ?? ACTION_NAMES[entry.action_id] ?? String(entry.action_id);
              const colors = ACTION_COLORS[actionName] ?? { bg: 'var(--gray-100)', color: 'var(--gray-700)' };
              const displayLabel = ACTION_DISPLAY[actionName] ?? actionName;
              const isExpanded = expandedRow === entry.id;
              const hasDetail = entry.old_value || entry.new_value || entry.notes;

              // Fix 2: show name only, no email sub-line
              const changedByDisplay = entry.changed_by_name
                || entry.changed_by_email?.split('@')[0]
                || '—';

              return (
                <React.Fragment key={entry.id}>
                  <tr
                    onClick={() => hasDetail && setExpandedRow(isExpanded ? null : entry.id)}
                    style={{ cursor: hasDetail ? 'pointer' : 'default' }}
                  >
                    <td style={{ width: 24, color: 'var(--gray-500)' }}>
                      {hasDetail && (isExpanded
                        ? <ChevronDown size={14} />
                        : <ChevronRightIcon size={14} />
                      )}
                    </td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      {new Date(entry.changed_at).toLocaleString()}
                    </td>
                    <td>
                      <span style={{
                        background: colors.bg, color: colors.color,
                        fontSize: 12, fontWeight: 700, padding: '2px 8px',
                        borderRadius: 4, whiteSpace: 'nowrap',
                      }}>
                        {displayLabel}
                      </span>
                    </td>
                    <td style={{ fontSize: 14 }}>{entry.entity_type}</td>
                    <td style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--gray-500)' }}>
                      {entry.entity_id ? `${entry.entity_id.slice(0, 8)}…` : '—'}
                    </td>
                    {/* Fix 2: name only */}
                    <td style={{ fontSize: 14, fontWeight: 500 }}>{changedByDisplay}</td>
                    <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{entry.ip_address ?? '—'}</td>
                  </tr>
                  {isExpanded && (
                    <tr style={{ background: 'var(--gray-50)' }}>
                      <td colSpan={7} style={{ padding: '14px 24px' }}>
                        {/* Fix 3: plain-language diff instead of raw JSON */}
                        {renderDiff(entry.old_value, entry.new_value, entry.notes)}
                        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--gray-500)' }}>
                          Entity ID: <code>{entry.entity_id ?? '—'}</code>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16 }}>
          <button
            className="btn btn-secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            <ChevronLeft size={14} /> Prev
          </button>
          <span style={{ fontSize: 14 }}>Page {currentPage} of {totalPages}</span>
          <button
            className="btn btn-secondary"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditLogPage;
